import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  CalendarIcon,
  MapPinIcon,
  StarIcon,
  XMarkIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  InformationCircleIcon,
  ClockIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline'
import { usePendingProviders, useApproveProvider, useRejectProvider } from '../../../hooks/useProviders'
import { useAdminAllDocuments } from '../../../hooks/useDocuments'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { ServiceProvider, ServiceType } from '../../../types'
import toast from 'react-hot-toast'

const serviceTypeLabels: Record<ServiceType, string> = {
  AMBULANCE_PROVIDER: 'Ambulance',
  USTADZ: 'Ustadz',
  CEMETERY_PROVIDER: 'Cemetery',
  VENDOR: 'Vendor',
  PROVIDER: 'General Provider',
}

const serviceTypeColors: Record<ServiceType, string> = {
  AMBULANCE_PROVIDER: 'bg-blue-100 text-blue-800',
  USTADZ: 'bg-purple-100 text-purple-800',
  CEMETERY_PROVIDER: 'bg-green-100 text-green-800',
  VENDOR: 'bg-orange-100 text-orange-800',
  PROVIDER: 'bg-gray-100 text-gray-800',
}

export default function PendingApprovalPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)

  const { data: pendingProviders = [], isLoading, error, refetch } = usePendingProviders()
  const { data: documents = [], isLoading: documentsLoading } = useAdminAllDocuments()
  const approveProviderMutation = useApproveProvider()
  const rejectProviderMutation = useRejectProvider()

  const filteredProviders = pendingProviders.filter(provider =>
    (provider.companyName && provider.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    provider.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (provider.city && provider.city.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  // Check document compliance for a provider
  const checkDocumentCompliance = (provider: ServiceProvider) => {
    if (!documents.length) {
      return {
        isCompliant: false,
        missingDocuments: [],
        unverifiedDocuments: [],
        totalRequired: 0,
        totalVerified: 0
      }
    }

    // Get documents for this specific provider
    const providerDocuments = documents.filter(doc =>
      doc.serviceProvider?.id === provider.id
    )

    // Define required documents by service type (matching backend logic)
    const getRequiredDocuments = (serviceType: string) => {
      switch (serviceType) {
        case 'AMBULANCE_TRANSPORT':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION']
        case 'GHUSL_SERVICE':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE']
        case 'KAFAN_SERVICE':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'JANAZAH_LEAD':
          return ['PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION']
        case 'BURIAL_PLOT':
          return ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION']
        case 'CATERING':
          return ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT']
        case 'FLOWERS':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'TENT_RENTAL':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'PHOTOGRAPHY':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'PRINTING':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        case 'ISLAMIC_SOUVENIRS':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        // Legacy provider types
        case 'CEMETERY_PROVIDER':
          return ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION']
        case 'USTADZ':
          return ['PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION']
        case 'VENDOR':
        case 'PROVIDER':
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
        default:
          return ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT']
      }
    }

    const requiredDocuments = getRequiredDocuments(provider.serviceType)
    const uploadedDocuments = providerDocuments.map(doc => doc.type)

    // Find missing required documents
    const missingDocuments = requiredDocuments.filter(docType => !uploadedDocuments.includes(docType))

    // Find unverified documents (uploaded but not verified)
    const unverifiedDocuments = providerDocuments
      .filter(doc => doc.status !== 'VERIFIED')
      .map(doc => doc.type)

    const totalVerified = providerDocuments.filter(doc => doc.status === 'VERIFIED').length

    return {
      isCompliant: missingDocuments.length === 0 && unverifiedDocuments.length === 0,
      missingDocuments,
      unverifiedDocuments,
      totalRequired: requiredDocuments.length,
      totalVerified
    }
  }

  // Get document type labels
  const documentTypeLabels: Record<string, string> = {
    BUSINESS_LICENSE: 'Business License',
    PROFESSIONAL_CERTIFICATE: 'Professional Certificate',
    INSURANCE_DOCUMENT: 'Insurance Document',
    IDENTIFICATION: 'Identification Document',
    OPERATING_PERMIT: 'Operating Permit',
    VEHICLE_REGISTRATION: 'Vehicle Registration',
    SERVICE_CERTIFICATION: 'Service Certification',
    OTHER: 'Other Document'
  }

  const handleApprove = async (providerId: string) => {
    try {
      await approveProviderMutation.mutateAsync(providerId)
      refetch()
    } catch (error: any) {
      console.error('Failed to approve provider:', error)

      // Check for detailed document verification errors
      if (error.response?.data?.message?.includes('documents are missing or not verified')) {
        const errorData = error.response.data
        const missingDocs = errorData.missingDocuments || []
        const unverifiedDocs = errorData.unverifiedDocuments || []

        let errorMessage = 'Provider cannot be approved until document requirements are met:\n\n'

        if (missingDocs.length > 0) {
          errorMessage += `Missing required documents: ${missingDocs.map(doc => documentTypeLabels[doc] || doc).join(', ')}\n`
        }

        if (unverifiedDocs.length > 0) {
          errorMessage += `Unverified documents: ${unverifiedDocs.map(doc => documentTypeLabels[doc] || doc).join(', ')}\n`
        }

        errorMessage += '\nPlease verify all required documents before approving.'

        toast.error(errorMessage, { duration: 8000 })
      } else {
        toast.error(error.response?.data?.message || 'Failed to approve provider')
      }
    }
  }

  const handleReject = async (providerId: string, reason: string) => {
    try {
      await rejectProviderMutation.mutateAsync({ providerId, reason })
      setShowRejectModal(false)
      setRejectionReason('')
      setSelectedProvider(null)
      refetch()
    } catch (error) {
      console.error('Failed to reject provider:', error)
    }
  }

  const openRejectModal = (provider: ServiceProvider) => {
    setSelectedProvider(provider)
    setShowRejectModal(true)
  }

  const closeRejectModal = () => {
    setShowRejectModal(false)
    setSelectedProvider(null)
    setRejectionReason('')
  }

  const openDetailModal = (provider: ServiceProvider) => {
    setSelectedProvider(provider)
    setShowDetailModal(true)
  }

  const closeDetailModal = () => {
    setShowDetailModal(false)
    setSelectedProvider(null)
  }

  const handleCopyProviderId = async (providerId: string) => {
    try {
      await navigator.clipboard.writeText(providerId)
      console.log('Provider ID copied to clipboard')
    } catch (err) {
      console.error('Failed to copy provider ID:', err)
    }
  }

  if (isLoading || documentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium">Failed to load pending providers</div>
        <p className="text-gray-500 mt-2">Please try again later</p>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pending Provider Approval</h1>
            <p className="text-gray-600 mt-1">Review and approve provider applications</p>
          </div>
          <Link
            to="/admin/providers"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Back to Overview
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center">
          <UserGroupIcon className="h-8 w-8 text-yellow-600" />
          <div className="ml-4">
            <p className="text-2xl font-bold text-gray-900">{pendingProviders.length}</p>
            <p className="text-gray-600">Pending Approvals</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Search by business name, email, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Pending Providers List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {filteredProviders.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No pending providers</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'No providers match your search criteria' : 'All providers have been reviewed'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredProviders.map((provider) => (
              <div key={provider.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {provider.companyName}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[provider.serviceType]}`}>
                        {serviceTypeLabels[provider.serviceType]}
                      </span>
                      <button
                        onClick={() => handleCopyProviderId(provider.id)}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors cursor-pointer"
                        title="Copy Provider ID"
                      >
                        <ClipboardDocumentIcon className="h-3 w-3 mr-1" />
                        ID: {provider.id.substring(0, 8)}...
                      </button>
                    </div>

                    <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Applied {new Date(provider.createdAt).toLocaleDateString()}
                      </div>
                      {provider.city && (
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {provider.city}
                        </div>
                      )}
                      {provider.rating && (
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 mr-1 text-yellow-400" />
                          {provider.rating.toFixed(1)}
                        </div>
                      )}
                    </div>

                    <div className="mt-2 text-sm text-gray-600">
                      <p className="font-medium">Contact: {provider.user.email}</p>
                      {provider.user.phone && <p>Phone: {provider.user.phone}</p>}
                      {provider.description && (
                        <p className="mt-1">{provider.description.substring(0, 200)}...</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {(() => {
                      const compliance = checkDocumentCompliance(provider)
                      return (
                        <button
                          onClick={() => handleApprove(provider.id)}
                          disabled={approveProviderMutation.isPending || !compliance.isCompliant}
                          className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                            compliance.isCompliant
                              ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                              : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                          }`}
                          title={!compliance.isCompliant ? `${compliance.missingDocuments.length + compliance.unverifiedDocuments.length} document(s) need attention` : 'Approve provider'}
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          {compliance.isCompliant ? 'Approve' : 'Documents Needed'}
                        </button>
                      )
                    })()}
                    <button
                      onClick={() => openRejectModal(provider)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      Reject
                    </button>
                    <Link
                      to={`/admin/providers/pending/${provider.id}`}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {showRejectModal && selectedProvider && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <XCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Reject Provider Application
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to reject the application from {selectedProvider.companyName}?
                        This action cannot be undone.
                      </p>
                    </div>
                    <div className="mt-4">
                      <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700">
                        Rejection Reason
                      </label>
                      <textarea
                        id="rejection-reason"
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Please provide a reason for rejection..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => handleReject(selectedProvider.id, rejectionReason)}
                  disabled={!rejectionReason.trim() || rejectProviderMutation.isPending}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Reject Application
                </button>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Provider Detail Modal */}
      {showDetailModal && selectedProvider && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl leading-6 font-medium text-gray-900">
                    Provider Details
                  </h3>
                  <button
                    onClick={closeDetailModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <UserGroupIcon className="h-5 w-5 text-primary-600 mr-2" />
                      <h4 className="text-lg font-medium text-gray-900">User Information</h4>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Contact Person</p>
                        <p className="text-base text-gray-900">{selectedProvider.user.firstName} {selectedProvider.user.lastName}</p>
                      </div>

                      <div className="flex items-center">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Email</p>
                          <p className="text-base text-gray-900">{selectedProvider.user.email}</p>
                        </div>
                      </div>

                      {selectedProvider.user.phone && (
                        <div className="flex items-center">
                          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Phone</p>
                            <p className="text-base text-gray-900">{selectedProvider.user.phone}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Account Status</p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            selectedProvider.verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {selectedProvider.verified ? 'Verified' : 'Not Verified'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Registered</p>
                          <p className="text-base text-gray-900">
                            {new Date(selectedProvider.registeredAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Provider Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
                      <h4 className="text-lg font-medium text-gray-900">Business Information</h4>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Business Name</p>
                        <p className="text-base text-gray-900">{selectedProvider.companyName}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-500">Provider ID</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <p className="text-base text-gray-900 font-mono">{selectedProvider.id}</p>
                          <button
                            onClick={() => handleCopyProviderId(selectedProvider.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy Provider ID"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-medium text-gray-500">Service Type</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${serviceTypeColors[selectedProvider.serviceType]}`}>
                          {serviceTypeLabels[selectedProvider.serviceType]}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Location</p>
                          <p className="text-base text-gray-900">{selectedProvider.city || 'Not specified'}</p>
                        </div>
                      </div>

                      {selectedProvider.rating && (
                        <div className="flex items-center">
                          <StarIcon className="h-4 w-4 text-yellow-400 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-500">Rating</p>
                            <p className="text-base text-gray-900">{selectedProvider.rating.toFixed(1)} / 5.0</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-500">Application Date</p>
                          <p className="text-base text-gray-900">
                            {new Date(selectedProvider.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedProvider.description && (
                  <div className="mt-6 bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center mb-3">
                      <InformationCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                      <h4 className="text-lg font-medium text-gray-900">Business Description</h4>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedProvider.description}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      openRejectModal(selectedProvider)
                      closeDetailModal()
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Reject Application
                  </button>
                  {(() => {
                    const compliance = selectedProvider ? checkDocumentCompliance(selectedProvider) : { isCompliant: false }
                    return (
                      <button
                        onClick={() => {
                          handleApprove(selectedProvider.id)
                          closeDetailModal()
                        }}
                        disabled={approveProviderMutation.isPending || !compliance.isCompliant}
                        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                          compliance.isCompliant
                            ? 'text-white bg-green-600 hover:bg-green-700 focus:ring-green-500'
                            : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                        }`}
                        title={!compliance.isCompliant ? `${compliance.missingDocuments.length + compliance.unverifiedDocuments.length} document(s) need attention` : 'Approve provider'}
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        {compliance.isCompliant ? 'Approve Application' : 'Documents Required'}
                      </button>
                    )
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}